import { useEffect, useMemo, useState } from "react";
import Color from "colorjs.io";
import { deriveSecondary } from "@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb";

export type ChatThemeId = "imprint" | "whisper" | "edge";

const HG_BRAND_PRIMARY = "#4E9CFF";
const HG_BRAND_SECONDARY = "#FF4DB8";

const isValidThemeId = (value: unknown): value is ChatThemeId =>
  value === "imprint" || value === "whisper" || value === "edge";

// Migrate any legacy v1 storage value (branding/soft/secondary/dark/contrast)
// to the new bubble-free vocabulary so existing users land on Imprint by default.
const migrateLegacyTheme = (raw: unknown): ChatThemeId => {
  if (isValidThemeId(raw)) return raw;
  if (raw === "soft") return "whisper";
  return "imprint";
};

// Keep brand-tinted *text* legible on white. Pale brands (e.g. amber) wash out
// as small labels, so darken the lightness while preserving the hue. Vivid
// dots/borders keep the raw brand — this floor is for ink only.
export const ensureReadableOnWhite = (hex: string): string => {
  try {
    const hsl = new Color(hex).to("hsl");
    if (hsl.coords[2] > 62) {
      hsl.coords[2] = 62;
      return hsl.to("srgb").toString({ format: "hex" });
    }
    return hex;
  } catch {
    return hex;
  }
};

// The dark-background counterpart: deep brands sink into a dark page as small
// labels, so lift the lightness while preserving the hue.
export const ensureReadableOnDark = (hex: string): string => {
  try {
    const hsl = new Color(hex).to("hsl");
    if (hsl.coords[2] < 68) {
      hsl.coords[2] = 68;
      return hsl.to("srgb").toString({ format: "hex" });
    }
    return hex;
  } catch {
    return hex;
  }
};

/** Brand-tinted ink readable on the chat's background, light or dark. */
export const readableInk = (hex: string, darkSurface = false): string =>
  darkSurface ? ensureReadableOnDark(hex) : ensureReadableOnWhite(hex);

/** Message ink on a light / dark chat background. */
const LIGHT_SURFACE_TEXT = "#111827";
const DARK_SURFACE_TEXT = "#F1F5F9";

export interface ChatThemeColors {
  style: "plain" | "tinted" | "edged";
  userBubbleBg: string;
  userTextColor: string;
  genieBubbleBg: string;
  genieTextColor: string;
  systemTextColor: string;
  userLabelColor: string;
  genieLabelColor: string;
  userRuleColor: string;
  genieRuleColor: string;
}

/**
 * Shared "Imprint / Whisper / Edge" chat theme — the color-token derivation,
 * persisted preference, and font-size preset live here so
 * GenieTextChatOverlayWeb (widget/split-view chat) and GenieConversationCanvas
 * (full-text desktop view) render the same theme choice identically, from the
 * same picker. Each caller supplies its own storage key (their persisted
 * settings shapes differ slightly) and applies themeColors/fontSize to its own
 * message layout.
 */
export const useChatTheme = ({
  storageKey,
  brandColor,
  secondaryColor,
  defaultFontSizeIndex = 1,
  fontSizePresets,
  darkSurface = false,
}: {
  storageKey: string;
  brandColor: string;
  secondaryColor?: string | null;
  defaultFontSizeIndex?: number;
  fontSizePresets: number[];
  /** The chat sits on a dark background: light message inks, stronger bubbles. */
  darkSurface?: boolean;
}) => {
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [fontSizeIndex, setFontSizeIndex] = useState<number>(defaultFontSizeIndex);
  const [themeId, setThemeId] = useState<ChatThemeId>("imprint");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { fontSizeIndex?: number; themeId?: unknown };
        if (typeof parsed.fontSizeIndex === "number") {
          const safeIndex =
            parsed.fontSizeIndex >= 0 && parsed.fontSizeIndex < fontSizePresets.length
              ? parsed.fontSizeIndex
              : defaultFontSizeIndex;
          setFontSizeIndex(safeIndex);
        }
        setThemeId(migrateLegacyTheme(parsed.themeId));
      }
    } catch (error) {
      console.warn(`[useChatTheme] Failed to load chat settings (${storageKey}):`, error);
    } finally {
      setSettingsLoaded(true);
    }
    // Only ever loaded once, on mount, per storageKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!settingsLoaded || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ fontSizeIndex, themeId }));
    } catch (error) {
      console.warn(`[useChatTheme] Failed to persist chat settings (${storageKey}):`, error);
    }
  }, [storageKey, fontSizeIndex, themeId, settingsLoaded]);

  const themeColors: ChatThemeColors = useMemo(() => {
    // Tenant brand drives the user side; genie side uses tenant secondary if set,
    // else derives an analogous shift from the brand so single-color tenants
    // still get a distinct genie label without HG pink bleeding through.
    const userBrand = brandColor || HG_BRAND_PRIMARY;
    const genieBrand =
      secondaryColor || (userBrand && userBrand !== HG_BRAND_PRIMARY ? deriveSecondary(userBrand) : HG_BRAND_SECONDARY);

    const text = darkSurface ? DARK_SURFACE_TEXT : LIGHT_SURFACE_TEXT;

    if (themeId === "whisper") {
      // Barely-there brand-tinted bubbles. Soft, but still recede into the page.
      // A dark page needs a stronger tint for the bubble to read as one.
      return {
        style: "tinted",
        userBubbleBg: darkSurface ? `${userBrand}4D` : `${userBrand}14`,
        userTextColor: text,
        genieBubbleBg: darkSurface ? "rgba(255, 255, 255, 0.08)" : "#F4F6FB",
        genieTextColor: text,
        systemTextColor: "#94A3B8",
        userLabelColor: userBrand,
        genieLabelColor: genieBrand,
        userRuleColor: userBrand,
        genieRuleColor: genieBrand,
      };
    }

    if (themeId === "edge") {
      // No bg. 2px brand-color rules on the gutter side of each message.
      return {
        style: "edged",
        userBubbleBg: "transparent",
        userTextColor: text,
        genieBubbleBg: "transparent",
        genieTextColor: text,
        systemTextColor: "#94A3B8",
        userLabelColor: userBrand,
        genieLabelColor: genieBrand,
        userRuleColor: userBrand,
        genieRuleColor: genieBrand,
      };
    }

    // Default: imprint. Text sits on the page like ChatGPT/Claude.
    return {
      style: "plain",
      userBubbleBg: "transparent",
      userTextColor: text,
      genieBubbleBg: "transparent",
      genieTextColor: text,
      systemTextColor: "#94A3B8",
      userLabelColor: userBrand,
      genieLabelColor: genieBrand,
      userRuleColor: userBrand,
      genieRuleColor: genieBrand,
    };
  }, [brandColor, secondaryColor, themeId, darkSurface]);

  const fontSize = fontSizePresets[fontSizeIndex] ?? fontSizePresets[defaultFontSizeIndex];
  const lineHeight = Math.round(fontSize * 1.55);
  const userLabelInk = useMemo(
    () => readableInk(themeColors.userLabelColor, darkSurface),
    [themeColors.userLabelColor, darkSurface],
  );
  const genieLabelInk = useMemo(
    () => readableInk(themeColors.genieLabelColor, darkSurface),
    [themeColors.genieLabelColor, darkSurface],
  );

  return {
    themeId,
    setThemeId,
    fontSizeIndex,
    setFontSizeIndex,
    themeColors,
    fontSize,
    lineHeight,
    userLabelInk,
    genieLabelInk,
  };
};
